import { formatColor } from '@meitu/saas-colors';
import { trackConfig, type IFramework } from '@/framework';
import { LayerType, type GradientColorResult, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { pushHistory, requestTextSVGPreview, updateLayer, validateInputLayer } from '../utils';

const COLOR_FUNC_ID = trackConfig[LayerType.TEXT_LAYER].color;

export class FillGradient {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    private prepare(prevLayer: ILayerProps, gradient: string): ILayerProps {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        if (prevLayerData.shapeExtension) return prevLayer;

        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            textGradient: formatColor(gradient) as GradientColorResult,
            texture: undefined,
            lineThrough: false,
            underline: false,
        };

        return { ...prevLayer, data: nextLayerData };
    }

    private setSingle(prevLayer: ILayerProps, gradient: string, writeHistory: boolean) {
        if ((prevLayer.data as ITextLayerProps).shapeExtension) return;

        const nextLayer = this.prepare(prevLayer, gradient);

        requestTextSVGPreview(this.framework, writeHistory ? 'commit' : 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { activate: true, dataOnly: !writeHistory });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, COLOR_FUNC_ID);
            this.prevLayer = null;
        }

        return nextLayer;
    }

    private setGroup(prevLayer: ILayerProps, gradient: string, writeHistory: boolean) {
        const children = prevLayer.children;
        if (children.length === 0) return;

        const layers = children.map((child) => this.prepare(child, gradient));
        const nextLayer = { ...prevLayer, children: layers };

        requestTextSVGPreview(this.framework, writeHistory ? 'commit' : 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { activate: true, dataOnly: !writeHistory });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, COLOR_FUNC_ID);
            this.prevLayer = null;
        }

        return nextLayer;
    }

    setFillGradient(layerID: string, gradient: string, options: { writeHistory?: boolean } = {}) {
        const { writeHistory = false } = options;
        const prevLayer = this.prevLayer ?? this.framework.service.flatLayer.getLayer(layerID, true);
        this.prevLayer = prevLayer;

        if (!validateInputLayer(prevLayer)) return;

        const result =
            prevLayer.layerType === LayerType.GROUP_LAYER
                ? this.setGroup(prevLayer, gradient, writeHistory)
                : this.setSingle(prevLayer, gradient, writeHistory);

        return result;
    }
}
